import React from 'react';
import { useParams } from 'react-router-dom';
import { copyFor, routes } from 'config';
import { Text } from '@perch-labs/shrike';
import { timeSince } from '@perch-labs/warbler';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { BackLink } from 'components/back-link';
import { Hr, Page, Sources, SourceText } from 'styles';
import { EnergySourceBreakdown } from 'pages/usage/components/energy-source-breakdown';
import { ErrorPage } from 'components/errors';
import { LoadingPage } from 'components/loading';
import { useGridStats } from 'hooks/use-grid-stats.hook';
import { useImpactStats } from 'hooks/use-impact-stats.hook';
import { monthString } from 'utils/format';
import { EnergyMix } from './energy-mix';

export const getCopy = copyFor('usage.energyAnalysis');
const getBackLinkCopy = copyFor('navigation.backLink.dashboardLocations');

export const EnergyAnalysis = () => {
  const { planId } = useParams<{ planId?: string }>();
  const { usage } = routes({ planId });
  const {
    data: gridStats,
    loading: gridLoading,
    error: gridError,
  } = useGridStats();
  const {
    data: impactStats,
    loading: impactLoading,
    error: impactError,
  } = useImpactStats({ planId: Number(planId) });
  const fossilFuelsPercent =
    gridStats && Math.round(gridStats.energySummary.fossilFuels * 100);

  if (gridLoading || impactLoading) return <LoadingPage />;
  if (gridError || impactError || !gridStats || !impactStats) {
    return (
      <ErrorPage
        location={getBackLinkCopy('usage')}
        title={getCopy('title')}
        to={usage}
      />
    );
  }

  return (
    <>
      <BackLink to={usage} location={getBackLinkCopy('usage')} />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <Text textStyle="heading800" tag="h1" margin={{ bottom: '16px' }}>
          {getCopy('title')}
        </Text>
        <Text margin={{ bottom: '32px' }}>
          {getCopy('paragraph1', {
            kwh: impactStats.carbonEquivalency.kwh,
            months: monthString(
              timeSince(impactStats.createdAt || '', {
                units: 'months',
              }) as number
            ),
          })}
        </Text>
        <EnergySourceBreakdown
          energyBreakdown={gridStats.energyBreakdown}
          fuelTypeEquivalency={impactStats.fuelTypeEquivalency}
        />
        <Text margin={{ bottom: '16px', top: '32px' }}>
          {getCopy('paragraph2', { fossilFuelsPercent })}
        </Text>
        <Text margin={{ bottom: '32px' }}>{getCopy('paragraph3')}</Text>
        <EnergyMix
          sources={{
            ...gridStats.energySummary,
            ...gridStats.energyBreakdown,
          }}
        />
        <Text margin={{ bottom: '16px', top: '32px' }}>
          {getCopy('paragraph4')}
        </Text>
        <Text margin={{ bottom: '16px' }}>{getCopy('paragraph5')}</Text>
        <Text margin={{ bottom: '16px' }}>{getCopy('paragraph6')}</Text>
        <Text margin={{ bottom: '66px' }}>{getCopy('paragraph7')}</Text>
        <Hr />
        <Sources>
          {getCopy('sources').map((item: string) => (
            <SourceText key={item}>{item}</SourceText>
          ))}
        </Sources>
      </Page>
    </>
  );
};
